<script lang="ts">
    import { getImageSizeUrl } from "@/utils/getImageSizeUrl";
    import ReviewCheckCircleIcon from "@apps/reviews/components/icons/ReviewCheckCircleIcon.svelte";
    import ReviewRatingStars from "@apps/reviews/components/RatingStars/ReviewRatingStars.svelte";
    import ToggleVote from "@apps/reviews/components/ToggleVote.svelte";
    import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
    import { VotesState } from "@apps/reviews/stores/VotesStore";
    import { getHumanReadableDate } from "@lib/utils";

    import Badge from "@components/Badge/Badge.svelte";
    import Icon from "@components/Icon.svelte";
    import InfoIcon from "@components/icons/InfoIcon.svelte";
    import Image from "@components/Image/Image.svelte";
    import Slider from "@components/Slider/Slider.svelte";
    import SchemaSliderController from "@components/Slider/SliderController";
    import T from "@components/T.svelte";
    import TooltipActivator from "@components/Tooltip/TooltipActivator.svelte";

    import ReviewsProduct from "../../ReviewsProduct.svelte";
    import ReviewsUnavailableProduct from "../../ReviewsUnavailableProduct.svelte";
    import ReviewsMasonryItemFrame from "./ReviewsMasonryItemFrame.svelte";

    export let namespace: string;
    export let review: ReviewEntity;
    export let customerName: string;
    export let showTitle: boolean;
    export let showProducts: boolean;
    export let votesEnabled: boolean;
    export let reviewVotes: VotesState | undefined;
    export let isIncentivizedEnabled: boolean;

    const sliderController = new SchemaSliderController();
</script>

<ReviewsMasonryItemFrame on:openReview>
    <svelte:fragment slot="media">
        <div class="gw-rv-item__media">
            {#if review.images.length}
                <Slider {sliderController} perPage={1} loop={false}>
                    <svelte:fragment slot="slides">
                        {#each review.images as image (image.id)}
                            <div class="gw-rv-item__media-img-wrapper">
                                <Image
                                    ratio={282 / 351}
                                    alt={review.title || ""}
                                    src={getImageSizeUrl(image.sizes, "md") ||
                                        ""}
                                    objectFit="cover"
                                    on:imageLoaded
                                />
                            </div>
                        {/each}
                    </svelte:fragment>
                </Slider>
            {/if}
        </div>
    </svelte:fragment>

    <ReviewRatingStars
        width={"16"}
        height={"16"}
        rating={review.rating}
        slot="rating"
    />

    <span class="gw-rv-item__date" slot="date">
        {getHumanReadableDate(review.createdAt)}
    </span>

    <svelte:fragment slot="username">
        <span class="gw-rv-item__username">
            {customerName}
        </span>
        {#if review?.customer?.isVerifiedBuyer}
            <div class="gw-rv-item__verified-buyer">
                <Badge
                    variant="regular"
                    icon={ReviewCheckCircleIcon}
                    size="md"
                    transparent
                    alignCenter
                >
                    <T {namespace} translationKey="verified_buyer" />
                </Badge>
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="title">
        {#if (showTitle && review.title) || (review.isIncentivized && isIncentivizedEnabled)}
            <div class="gw-rv-item__title-bar">
                {#if showTitle && review.title}
                    <h3 class="gw-rv-item__title">{review.title}</h3>
                {/if}
                {#if isIncentivizedEnabled && review.isIncentivized}
                    <div class="gw-rv-item__incentivized-wrapper">
                        <TooltipActivator position="top-end">
                            <div class="gw-rv-item__incentivized">
                                <T {namespace} translationKey="incentivized" />

                                <Icon
                                    width="14"
                                    height="14"
                                    color="#12b76a"
                                    stroke="#12b76a"
                                >
                                    <InfoIcon />
                                </Icon>
                            </div>

                            <T
                                slot="content"
                                {namespace}
                                translationKey="incentivized_content"
                            />
                        </TooltipActivator>
                    </div>
                {/if}
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="text">
        {review.body}
    </svelte:fragment>

    <svelte:fragment slot="vote">
        {#if votesEnabled && reviewVotes}
            <ToggleVote {reviewVotes} on:toggleVote />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="footer">
        {#if review.product?.isDraft || review.product?.isDeleted}
            <ReviewsUnavailableProduct
                productId={review.product.id}
                token={review.product?.unavailableProductToken || ""}
                on:productReady
            />
        {:else if review.product?.id && showProducts}
            <ReviewsProduct
                productId={review.product.id}
                on:productReady
                on:imageLoaded
            />
        {/if}
    </svelte:fragment>
</ReviewsMasonryItemFrame>

<style lang="postcss">
    .gw-rv-item {
        &__username {
            font-size: 1em;
            font-weight: 600;
        }

        &__date {
            color: var(--gw-lightgrey-text-color);
            font-size: 0.75em;
        }

        &__title-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: nowrap;
            gap: 0.75rem;
            margin-top: 0.75em;
        }

        &__title {
            width: 100%;
            margin: 0;
            font-size: 1em;
            font-weight: 600;
            line-height: 20px;
        }

        &__incentivized-wrapper {
            display: flex;
            justify-content: flex-end;
            flex: 1;
        }

        &__incentivized {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            font-size: 0.875em;
            font-style: normal;
            font-weight: 400;
            line-height: 1.4;
            color: var(--gw-lightgrey-text-color, #8a96a1);
        }
    }
</style>
